/**
 * Primitive guards + accumulator helpers used by every Voluum validator.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ValidationError, ValidationErrorCode } from './errors';

/** Mutable accumulator that the per-resource validators push onto. */
export interface ValidationCtx {
  errors: ValidationError[];
}

export function pushError(
  ctx: ValidationCtx,
  path: string,
  code: ValidationErrorCode,
  message: string,
  extra?: { expected?: string; received?: string },
): void {
  ctx.errors.push({ path, code, message, ...extra });
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

export function isNonEmptyArray(v: unknown): v is unknown[] {
  return Array.isArray(v) && v.length > 0;
}

export function describe(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return `array(${v.length})`;
  return typeof v;
}

export function joinPath(base: string, key: string | number): string {
  if (typeof key === 'number') return `${base}[${key}]`;
  return base.length === 0 ? key : `${base}.${key}`;
}

/** Require key, with type and (optional) enum check. Returns the value when valid. */
export function requireString(
  ctx: ValidationCtx,
  obj: Record<string, unknown>,
  key: string,
  basePath: string,
  enumValues?: readonly string[],
): string | undefined {
  const path = joinPath(basePath, key);
  const v = obj[key];
  if (v === undefined) {
    pushError(ctx, path, 'missing_required', `Missing required field "${key}"`);
    return undefined;
  }
  if (typeof v !== 'string') {
    pushError(
      ctx,
      path,
      'wrong_type',
      `Expected string for "${key}"`,
      { expected: 'string', received: describe(v) },
    );
    return undefined;
  }
  if (enumValues && !enumValues.includes(v)) {
    pushError(
      ctx,
      path,
      'invalid_enum',
      `"${v}" is not a valid value for "${key}"`,
      { expected: enumValues.join(' | '), received: v },
    );
    return undefined;
  }
  return v;
}

/** Require boolean. */
export function requireBoolean(
  ctx: ValidationCtx,
  obj: Record<string, unknown>,
  key: string,
  basePath: string,
): boolean | undefined {
  const path = joinPath(basePath, key);
  const v = obj[key];
  if (v === undefined) {
    pushError(ctx, path, 'missing_required', `Missing required field "${key}"`);
    return undefined;
  }
  if (typeof v !== 'boolean') {
    pushError(ctx, path, 'wrong_type', `Expected boolean for "${key}"`, {
      expected: 'boolean',
      received: describe(v),
    });
    return undefined;
  }
  return v;
}

/** Require finite number. */
export function requireNumber(
  ctx: ValidationCtx,
  obj: Record<string, unknown>,
  key: string,
  basePath: string,
): number | undefined {
  const path = joinPath(basePath, key);
  const v = obj[key];
  if (v === undefined) {
    pushError(ctx, path, 'missing_required', `Missing required field "${key}"`);
    return undefined;
  }
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    pushError(ctx, path, 'wrong_type', `Expected finite number for "${key}"`, {
      expected: 'number',
      received: describe(v),
    });
    return undefined;
  }
  return v;
}
